import React, { useEffect, useRef, useState } from "react";

/**
 * LazyVideo — mounts the <video> element only when scrolled into view,
 * and pauses when scrolled out. Muted + inline + loop for autoplay support.
 * Uses a poster image so first paint is instant while the mp4 streams in.
 */
export default function LazyVideo({
  src,
  poster,
  className = "",
  objectPosition = "center",
  ariaLabel,
  testId,
}) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!wrapRef.current || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          videoRef.current?.play().catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      },
      { rootMargin: "200px 0px", threshold: 0.15 }
    );
    io.observe(wrapRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className={`overflow-hidden ${className}`} data-testid={testId}>
      {/* Poster is always painted first for instant visual */}
      {poster && (
        <img
          src={poster}
          alt={ariaLabel || ""}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition }}
          loading="lazy"
          decoding="async"
        />
      )}
      {inView && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition }}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          aria-label={ariaLabel}
          aria-hidden={ariaLabel ? undefined : "true"}
          disableRemotePlayback
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
    </div>
  );
}
